import React, { useEffect, useState } from 'react';
import { Share, SquarePlus, X, Download } from 'lucide-react';

const DISMISS_KEY = 'ca_a2hs_dismissed';

const isiOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export const AddToHomeBanner = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    const isMobile =
      window.matchMedia('(max-width: 767px)').matches ||
      window.matchMedia('(pointer: coarse)').matches ||
      isiOS();
    if (!isMobile) return;

    if (isiOS()) {
      // iOS Safari never fires beforeinstallprompt — show a manual hint after a delay.
      setIos(true);
      const t = setTimeout(() => setVisible(true), 3500);
      return () => clearTimeout(t);
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => dismiss();
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (e) { console.debug('a2hs dismiss store failed:', e?.message || e); }
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) { console.debug('a2hs prompt dismissed:', e?.message || e); }
    setDeferred(null);
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      data-testid="a2hs-banner"
      className="fixed inset-x-3 bottom-3 z-[1200] sm:hidden animate-in slide-in-from-bottom-4 fade-in duration-300"
    >
      <div className="glass-panel rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3">
        <img src="/icon-192.png" alt="CurbAlert" className="w-10 h-10 rounded-xl shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-display text-sm font-semibold text-brand-ink leading-tight">Add CurbAlert to your Home Screen</p>
          {ios ? (
            <p className="text-xs text-brand-ink/70 mt-0.5 flex items-center gap-1 flex-wrap">
              Tap <Share className="inline w-3.5 h-3.5 text-brand" /> then
              <span className="inline-flex items-center gap-0.5">
                <SquarePlus className="inline w-3.5 h-3.5 text-brand" /> Add to Home Screen
              </span>
            </p>
          ) : (
            <p className="text-xs text-brand-ink/70 mt-0.5">Quick access &amp; nearby-item alerts, like an app.</p>
          )}
        </div>
        {!ios && (
          <button
            data-testid="a2hs-install-btn"
            onClick={install}
            className="shrink-0 rounded-full bg-brand text-white px-3 py-2 text-sm font-medium flex items-center gap-1.5 active:scale-95 transition-transform"
          >
            <Download className="w-4 h-4" /> Add
          </button>
        )}
        <button
          data-testid="a2hs-dismiss-btn"
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded-full p-1.5 text-brand-ink/50 hover:text-brand-ink hover:bg-black/5 transition"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default AddToHomeBanner;
